/**
 * WorkoutField Component
 * Renders a workout block with its exercises using React Hook Form
 * Requirements: 1.3, 4.5, 6.1, 6.2
 */

import * as React from 'react';
import {memo, useCallback, useState} from 'react';
import { Input as TextInput, MarkdownEditor } from '../../../../../../components/ui';
// @ts-ignore - react-input-mask doesn't have types
import InputMask from 'react-input-mask';
import {Controller} from 'react-hook-form';
import {
  WorkoutContainer,
  WorkoutContainerHead,
  WorkoutContainerBody,
  WorkoutContainerEmpty,
  WorkoutExerciseTitle,
} from '../../../../../components/workouts/WorkoutComponent.style';
import {
  WorkoutToggleContainer,
  WorkoutGroupContainerMinified,
} from '../../../../../components/workouts/WorkoutGroups.style';
import {
  ExerciseContainer,
  ExerciseContainerBody,
} from '../../../../../components/exercises/ExerciseComponents.style';
import {
  ButtonDeleteComponent,
  ButtonToggleContentClean,
  ButtonState,
} from '../../../../../components/button';
import {ButtonRowHeaderContainer} from '../../../../../components/button/Button.style';
import {useWorkoutField} from './WorkoutField.use-case';
import {ExerciseField} from '../ExerciseField/ExerciseField';
import type {WorkoutFieldProps} from './WorkoutField.type';

/**
 * WorkoutField Component
 * Manages a single workout block with its nested exercises
 * Uses React.memo for performance optimization (Requirement 6.2)
 */
export const WorkoutField = memo(function WorkoutField({
  form,
  groupIndex,
  workoutIndex,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  exerciseListDefault = [],
}: WorkoutFieldProps): JSX.Element {
  const {
    exercisesField,
    isExpanded,
    toggleExpanded,
    addExercise,
    removeExercise,
    moveExercise,
  } = useWorkoutField(form, groupIndex, workoutIndex);

  // State for exercise library modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get errors for this workout
  const errors =
    form.formState.errors?.workoutGroups?.[groupIndex]?.workouts?.[
      workoutIndex
    ];

  // Toggle button state based on expanded state
  const buttonState = isExpanded ? ButtonState.open : ButtonState.closed;

  // Handle toggle content
  const handleToggleContent = useCallback(
    (value: ButtonState) => {
      if (
        (value === ButtonState.open && !isExpanded) ||
        (value === ButtonState.closed && isExpanded)
      ) {
        toggleExpanded();
      }
    },
    [isExpanded, toggleExpanded],
  );

  // Handle opening the exercise library modal
  const handleOpenModal = useCallback((event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsModalOpen(true);
  }, []);

  // Handle closing the modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Handle selecting an exercise from the library
  const handleSelectExercise = useCallback(
    (exerciseData: any) => {
      // Add the exercise to the form
      const newExercise = {
        tempId: `temp_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 11)}`,
        exerciseId: exerciseData.exercise.id,
        exercise: {
          value: exerciseData.exercise.id,
          label: exerciseData.exercise.name,
          animatedImage: exerciseData.exercise.animatedImage,
          image: exerciseData.exercise.image,
        },
        repetitions: exerciseData.reps || '',
        rounds: exerciseData.series || '',
        load: exerciseData.weight || '',
        time: exerciseData.time || '',
        restTime: exerciseData.restTime || '',
        notes: exerciseData.notes || '',
      };

      exercisesField.append(newExercise);
      setIsModalOpen(false);
    },
    [exercisesField],
  );

  // Filter out destroyed exercises for display
  const visibleExercises = exercisesField.fields.filter((field, index) => {
    const exercise = form.getValues(
      `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${index}`,
    );
    return !exercise?._destroy;
  });

  // Dynamically import ExerciseLibraryModal to avoid circular dependencies
  const ExerciseLibraryModal = React.useMemo(() => {
    try {
      return require('../../../../../components/exercises/ExerciseLibraryModal')
        .ExerciseLibraryModal;
    } catch {
      return null;
    }
  }, []);

  return (
    <WorkoutContainer>
      <WorkoutContainerHead>
        <WorkoutToggleContainer>
          <i
            onClick={onMoveUp}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onMoveUp?.()}
            className="far fa-long-arrow-up"
            role="button"
            tabIndex={0}
            style={{
              opacity: isFirst ? 0.3 : 1,
              cursor: isFirst ? 'not-allowed' : 'pointer',
            }}
          />
          <i
            onClick={onMoveDown}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onMoveDown?.()}
            className="far fa-long-arrow-down"
            role="button"
            tabIndex={0}
            style={{
              opacity: isLast ? 0.3 : 1,
              cursor: isLast ? 'not-allowed' : 'pointer',
            }}
          />
        </WorkoutToggleContainer>

        {/* Name field with Controller for React Hook Form */}
        <Controller
          name={`workoutGroups.${groupIndex}.workouts.${workoutIndex}.name`}
          control={form.control}
          render={({field, fieldState}) => (
            <TextInput
              s={8}
              id={`workoutGroup-${groupIndex}-workout-${workoutIndex}`}
              onChange={field.onChange}
              value={field.value || ''}
              placeholder="Dê um nome para o bloco do treino (Ex: Aquecimento, força etc)"
              label={null}
              inputClassName={fieldState.error ? 'invalid' : ''}
              error={fieldState.error?.message}
            />
          )}
        />

        {/* Time field */}
        <div className="col s4 input-field">
          <Controller
            name={`workoutGroups.${groupIndex}.workouts.${workoutIndex}.time_text`}
            control={form.control}
            render={({field, fieldState}) => (
              <>
                <InputMask
                  id={`workoutGroup-${groupIndex}-workout-${workoutIndex}-time_text`}
                  name={field.name}
                  type="tel"
                  mask="99:99"
                  maskChar="0"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  value={field.value || ''}
                  placeholder="Defina um tempo"
                  className={fieldState.error ? 'invalid' : ''}
                />
                <span
                  className="helper-text"
                  data-error={fieldState.error?.message}></span>
              </>
            )}
          />
        </div>

        <ButtonRowHeaderContainer>
          <ButtonToggleContentClean
            onPress={handleToggleContent}
            buttonState={buttonState}
            title="Minimize o conteúdo para editar mais facilmente"
          />
          <ButtonDeleteComponent
            onPress={onRemove}
            name="Etapa do treino"
            confirmMessage="Você tem certeza que deseja apagar essa etapa de treino?"
            title="Apagar o treino, suas etapas e exercício"
          />
        </ButtonRowHeaderContainer>
      </WorkoutContainerHead>

      {isExpanded && (
        <WorkoutContainerBody>
          {/* Description field */}
          <Controller
            name={`workoutGroups.${groupIndex}.workouts.${workoutIndex}.description`}
            control={form.control}
            render={({field, fieldState}) => (
              <MarkdownEditor
                s={12}
                id={`workoutGroup-${groupIndex}-workout-${workoutIndex}-description`}
                onChange={field.onChange}
                value={field.value || ''}
                label="Descreva o bloco de treino ou observações"
                placeholder="Escreva aqui suas observações"
                error={fieldState.error?.message || Boolean(fieldState.error)}
              />
            )}
          />

          <div className="col s12">
            <WorkoutExerciseTitle>Exercícios</WorkoutExerciseTitle>
          </div>

          <div className="col s12">
            <ExerciseContainer>
              <ExerciseContainerBody>
                {exercisesField.fields.map((field, exerciseIndex) => {
                  const exercise = form.getValues(
                    `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}`,
                  );
                  // Skip destroyed exercises
                  if (exercise?._destroy) return null;

                  const visibleIndex = visibleExercises.findIndex(
                    (e) => e.fieldId === field.fieldId,
                  );

                  return (
                    <ExerciseField
                      key={field.fieldId}
                      form={form}
                      groupIndex={groupIndex}
                      workoutIndex={workoutIndex}
                      exerciseIndex={exerciseIndex}
                      onRemove={() => removeExercise(exerciseIndex)}
                      onMoveUp={
                        visibleIndex > 0
                          ? () => moveExercise(exerciseIndex, exerciseIndex - 1)
                          : undefined
                      }
                      onMoveDown={
                        visibleIndex < visibleExercises.length - 1
                          ? () => moveExercise(exerciseIndex, exerciseIndex + 1)
                          : undefined
                      }
                      isFirst={visibleIndex === 0}
                      isLast={visibleIndex === visibleExercises.length - 1}
                      exerciseListDefault={exerciseListDefault}
                    />
                  );
                })}

                {/* Add new exercise button */}
                <WorkoutContainer
                  className="clean-top add-new"
                  onClick={handleOpenModal}
                  style={{marginTop: '10px', cursor: 'pointer'}}>
                  <WorkoutContainerEmpty
                    as="button"
                    type="button"
                    onClick={handleOpenModal}
                    style={{
                      border: 'none',
                      background: 'none',
                      width: '100%',
                      cursor: 'pointer',
                    }}>
                    Adicionar exercício
                  </WorkoutContainerEmpty>
                </WorkoutContainer>
              </ExerciseContainerBody>
            </ExerciseContainer>
          </div>
        </WorkoutContainerBody>
      )}

      {!isExpanded && (
        <WorkoutGroupContainerMinified onClick={toggleExpanded}>
          <i className="far fa-ellipsis-h"></i>
        </WorkoutGroupContainerMinified>
      )}

      {/* Exercise Library Modal */}
      {ExerciseLibraryModal && (
        <ExerciseLibraryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSelectExercise={handleSelectExercise}
          workoutGroupIndex={groupIndex}
          workoutIndex={workoutIndex}
        />
      )}
    </WorkoutContainer>
  );
});

export default WorkoutField;
